import {
  ArrowRight,
  CalendarDays,
  Database,
  Info,
  RotateCcw,
  Sparkles,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import planinMarkUrl from "../../visualization/public/planin-logo-mark.png";
import { FlowMap } from "./components/FlowMap";
import { RegionPicker } from "./components/RegionPicker";
import type { OdData, TimeSetKey } from "./types";
import {
  compactTrips,
  flowId,
  flowsForSelection,
  formatTrips,
  rankRegions,
  regionByName,
} from "./utils";

const AIRPORT = "Chennai International Airport";

function LoadingState() {
  return (
    <main className="loading-screen" role="status">
      <img src={planinMarkUrl} alt="" />
      <div><span /><span /></div>
      <p>Loading Chennai O/D flows…</p>
    </main>
  );
}

function selectionSummary(values: string[], role: "origin" | "destination", data: OdData): string {
  if (values.length === 0) return `All ${role === "origin" ? "origins" : "destinations"}`;
  if (values.length === 1) return data.regions.find((region) => region.name === values[0])?.shortName ?? values[0];
  return `${values.length} zones`;
}

export default function App() {
  const [data, setData] = useState<OdData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [timeSet, setTimeSet] = useState<TimeSetKey>("allDay");
  const [origins, setOrigins] = useState<string[]>([]);
  const [destinations, setDestinations] = useState<string[]>([AIRPORT]);
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);
  const [hoveredFlowId, setHoveredFlowId] = useState<string | null>(null);
  const [flowLimit, setFlowLimit] = useState(30);

  useEffect(() => {
    fetch("/data/od-data.json")
      .then((response) => {
        if (!response.ok) throw new Error(`OD data request failed (${response.status})`);
        return response.json() as Promise<OdData>;
      })
      .then((payload) => {
        setData(payload);
        setDestinations([payload.meta.airportRegion]);
      })
      .catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "The dataset could not be loaded."));
  }, []);

  const lookup = useMemo(() => regionByName(data?.regions ?? []), [data?.regions]);
  const selectedFlows = useMemo(() => {
    if (!data) return [];
    return flowsForSelection(data.flowsByTimeSet[timeSet], origins, destinations);
  }, [data, destinations, origins, timeSet]);
  const totalTrips = selectedFlows.reduce((sum, flow) => sum + flow.t, 0);
  const originRanks = useMemo(() => rankRegions(selectedFlows, "origin"), [selectedFlows]);
  const destinationRanks = useMemo(() => rankRegions(selectedFlows, "destination"), [selectedFlows]);
  const largestTrips = Math.max(1, selectedFlows[0]?.t ?? 0);
  const isDefaultSelection = origins.length === 0 && destinations.length === 1 && destinations[0] === (data?.meta.airportRegion ?? AIRPORT);

  if (error) return <main className="error-screen"><h1>Chennai flows are unavailable</h1><p>{error}</p></main>;
  if (!data) return <LoadingState />;

  const reset = () => {
    setOrigins([]);
    setDestinations([data.meta.airportRegion]);
    setTimeSet("allDay");
    setFlowLimit(30);
    setHoveredRegion(null);
    setHoveredFlowId(null);
  };
  const topFlow = selectedFlows[0];

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="brand-lockup"><img src={planinMarkUrl} alt="" aria-hidden="true" /><span>PlaniN</span></div>
        <div className="title-lockup"><p>TomTom O/D analysis</p><h1>Chennai traffic flows</h1></div>
        <div className="report-context">
          <span><CalendarDays aria-hidden="true" />{data.meta.dateLabel}</span>
          <span><Database aria-hidden="true" />{data.meta.complete ? "Accepted report" : data.meta.analysisStatus}</span>
        </div>
      </header>

      {!data.meta.complete && (
        <div className="pending-banner" role="status"><Info aria-hidden="true" /><span>TomTom analysis {data.meta.analysisId} is {data.meta.analysisStatus.toLocaleLowerCase()}. Flow rows appear when its accepted matrices are available.</span></div>
      )}

      <main id="workspace" className="workspace">
        <aside className="control-rail" aria-label="Flow controls">
          <div className="rail-heading">
            <div><p className="eyebrow">Flow definition</p><h2>Choose movement roles</h2></div>
            {!isDefaultSelection && <button className="icon-button" type="button" aria-label="Reset flow selection" title="Reset to Chennai Airport destination" onClick={reset}><RotateCcw aria-hidden="true" /></button>}
          </div>
          <p className="rail-copy">Combine one or more zones. Empty roles include every Chennai zone in that direction.</p>

          <div className="role-fields">
            <RegionPicker role="origin" regions={data.regions} values={origins} onChange={setOrigins} />
            <RegionPicker role="destination" regions={data.regions} values={destinations} onChange={setDestinations} />
          </div>

          <div className="selection-readout" aria-live="polite">
            <span className="live-dot" aria-hidden="true" />
            <div><strong>{formatTrips(totalTrips)}</strong><small>{selectedFlows.length.toLocaleString("en-IN")} matching O/D pairs · {data.meta.odMultiplier}× factored weekday</small></div>
          </div>

          <section className="rail-section">
            <label className="field-label" htmlFor="time-set">Analysis period</label>
            <select id="time-set" value={timeSet} onChange={(event) => setTimeSet(event.target.value as TimeSetKey)}>
              {data.timeSets.map((item) => <option key={item.key} value={item.key}>{item.label} · {item.window}</option>)}
            </select>
          </section>

          <section className="rail-section">
            <div className="field-label-row"><label htmlFor="flow-limit">Map flow density</label><output htmlFor="flow-limit">Top {flowLimit}</output></div>
            <input id="flow-limit" className="range-control" type="range" min="10" max="80" step="10" value={flowLimit} onChange={(event) => setFlowLimit(Number(event.target.value))} />
            <div className="range-labels"><span>Clear</span><span>Dense</span></div>
          </section>

          <section className="rail-section role-key" aria-label="Map flow legend">
            <p className="toolbar-label">Movement roles</p>
            <span><i className="role-swatch origin" />Origin</span>
            <span><i className="role-swatch destination" />Destination</span>
            <span><i className="role-swatch combined" />Both roles</span>
          </section>

          <div className="motion-note"><Sparkles aria-hidden="true" /><span>Particle direction shows movement; line thickness reflects trip volume.</span></div>
        </aside>

        <section className="map-panel" aria-label="Chennai origin-destination flow map">
          <FlowMap
            data={data}
            flows={selectedFlows}
            flowLimit={flowLimit}
            origins={origins}
            destinations={destinations}
            hoveredRegion={hoveredRegion}
            hoveredFlowId={hoveredFlowId}
            onHoverRegion={setHoveredRegion}
            onHoverFlow={setHoveredFlowId}
            onSelectRegion={(name, role) => role === "destination" ? setDestinations([name]) : setOrigins([name])}
          />
        </section>

        <aside className="analysis-dock" aria-label="Selected flow analysis">
          <section className="analysis-hero">
            <p className="eyebrow">Factored selected movement</p>
            <strong>{compactTrips(totalTrips)}</strong>
            <span>TomTom trips / average weekday · {data.meta.odMultiplier}× O/D factor</span>
            <p>{selectedFlows.length.toLocaleString("en-IN")} active interzonal pairs match the current O/D definition.</p>
          </section>

          <section className="route-summary">
            <div><span className="role-marker origin">O</span><p><small>Origin</small><strong>{selectionSummary(origins, "origin", data)}</strong></p></div>
            <ArrowRight aria-hidden="true" />
            <div><span className="role-marker destination">D</span><p><small>Destination</small><strong>{selectionSummary(destinations, "destination", data)}</strong></p></div>
          </section>

          <section className="top-signal">
            <p className="eyebrow">Largest selected pair</p>
            {topFlow ? (
              <button
                type="button"
                onClick={() => { setOrigins([topFlow.o]); setDestinations([topFlow.d]); }}
                onMouseEnter={() => setHoveredFlowId(flowId(topFlow))}
                onMouseLeave={() => setHoveredFlowId(null)}
                onFocus={() => setHoveredFlowId(flowId(topFlow))}
                onBlur={() => setHoveredFlowId(null)}
              >
                <span>{lookup.get(topFlow.o)?.shortName ?? topFlow.o}<small>to {lookup.get(topFlow.d)?.shortName ?? topFlow.d}</small></span>
                <strong>{formatTrips(topFlow.t)}</strong>
              </button>
            ) : <p className="empty-state">No flows match this O/D combination.</p>}
          </section>

          <section className="role-rankings">
            <div className="section-heading"><div><p className="eyebrow">Selected network</p><h2>Role totals</h2></div><span>{data.timeSets.find((item) => item.key === timeSet)?.label}</span></div>
            <div className="role-ranking-grid">
              <div><h3><i className="role-swatch origin" />Origins</h3>{originRanks.slice(0, 5).map((item) => <button key={item.name} type="button" onClick={() => setOrigins([item.name])} onMouseEnter={() => setHoveredRegion(item.name)} onMouseLeave={() => setHoveredRegion(null)} onFocus={() => setHoveredRegion(item.name)} onBlur={() => setHoveredRegion(null)}><span>{lookup.get(item.name)?.shortName ?? item.name}</span><strong>{compactTrips(item.trips)}</strong></button>)}</div>
              <div><h3><i className="role-swatch destination" />Destinations</h3>{destinationRanks.slice(0, 5).map((item) => <button key={item.name} type="button" onClick={() => setDestinations([item.name])} onMouseEnter={() => setHoveredRegion(item.name)} onMouseLeave={() => setHoveredRegion(null)} onFocus={() => setHoveredRegion(item.name)} onBlur={() => setHoveredRegion(null)}><span>{lookup.get(item.name)?.shortName ?? item.name}</span><strong>{compactTrips(item.trips)}</strong></button>)}</div>
            </div>
          </section>

          <section className="ranking-section">
            <div className="section-heading"><div><p className="eyebrow">Flow ranking</p><h2>Largest O/D pairs</h2></div><span>Top 12</span></div>
            <div className="ranking-list">
              {selectedFlows.slice(0, 12).map((flow, index) => {
                const id = flowId(flow);
                return (
                  <button key={id} type="button" className={hoveredFlowId === id ? "is-selected" : ""} onClick={() => { setOrigins([flow.o]); setDestinations([flow.d]); }} onMouseEnter={() => setHoveredFlowId(id)} onMouseLeave={() => setHoveredFlowId(null)} onFocus={() => setHoveredFlowId(id)} onBlur={() => setHoveredFlowId(null)}>
                    <span className="rank">{String(index + 1).padStart(2, "0")}</span>
                    <span className="rank-label">{lookup.get(flow.o)?.shortName ?? flow.o}<small>→ {lookup.get(flow.d)?.shortName ?? flow.d}</small><i style={{ transform: `scaleX(${flow.t / largestTrips})` }} /></span>
                    <strong>{formatTrips(flow.t)}</strong>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="truth-note"><Info aria-hidden="true" /><p><strong>{data.meta.odMultiplier}× O/D factor applied</strong><span>Every TomTom O/D pair is multiplied by {data.meta.odMultiplier} after calculating the 23-weekday average. Curves indicate zone-to-zone movement, not driven road paths.</span></p></section>
        </aside>
      </main>

      <footer className="institutional-footer">PlanIn Lab - an IIT Incubated Innovations Lab</footer>
    </div>
  );
}
